import { Subject, Subscription } from 'rxjs';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { SnackBarService } from 'src/app/shared/services/snackBar.service';
import { ContextService } from './context.service';
import { ContextType } from './models/context.data';

@Injectable({ providedIn: 'root' })
export class ContextCrossTabSyncService implements OnDestroy {
  private initialized = false;
  private syncSequence = 0;
  private syncReadCancelled$ = new Subject<void>();
  private currentEstateId = '';
  private currentUserId = '';
  private currentContext: ContextType | null = null;
  private subscription$ = new Subscription();
  private readonly storageListener = (event: StorageEvent) => this.handleStorage(event);

  constructor(
    private ctxSrv: ContextService,
    private localStorageSrv: LocalStorageService,
    private snackBarService: SnackBarService,
    private zone: NgZone
  ) {
    this.subscription$.add(
      this.ctxSrv.getUserId$().subscribe((userId) => {
        ++this.syncSequence;
        this.syncReadCancelled$.next();
        this.currentUserId = userId ?? '';
      })
    );
    this.subscription$.add(
      this.ctxSrv.getContextType$().subscribe((context) => {
        ++this.syncSequence;
        this.syncReadCancelled$.next();
        this.currentContext = context;
      })
    );
    this.subscription$.add(this.ctxSrv.getEstateId$().subscribe((estateId) => (this.currentEstateId = estateId ?? '')));
  }

  public initialize(): void {
    if (this.initialized || typeof window === 'undefined') return;

    window.addEventListener('storage', this.storageListener);
    this.initialized = true;
  }

  ngOnDestroy(): void {
    ++this.syncSequence;
    this.syncReadCancelled$.next();
    this.subscription$.unsubscribe();
    this.syncReadCancelled$.complete();
    if (!this.initialized || typeof window === 'undefined') return;

    window.removeEventListener('storage', this.storageListener);
    this.initialized = false;
  }

  private handleStorage(event: StorageEvent): void {
    if (typeof localStorage === 'undefined' || event.storageArea !== localStorage) return;

    const userId = this.currentUserId;
    if (
      this.currentContext !== ContextType.ESTATE ||
      !userId ||
      event.key !== this.localStorageSrv.selectedEstate.storageKey(userId)
    ) {
      return;
    }

    const estateId = event.newValue;
    if (!estateId || estateId === this.currentEstateId) return;

    this.zone.run(() => void this.syncEstate(estateId));
  }

  private async syncEstate(estateId: string): Promise<void> {
    const sequence = ++this.syncSequence;
    this.syncReadCancelled$.next();
    const [, estateName] = await Promise.all([this.ctxSrv.setEstate(estateId, { persist: false }), this.getEstateName(estateId)]);
    if (sequence !== this.syncSequence) return;

    this.snackBarService.showInfo({ key: 'context.estateSyncedFromOtherTab', replaceMap: { $estate: estateName } }, 'OK');
  }

  private async getEstateName(estateId: string): Promise<string> {
    try {
      return (await ContextService.getEstateObject(this.ctxSrv)(estateId).snapshot(this.syncReadCancelled$))?.name ?? estateId;
    } catch {
      return estateId;
    }
  }
}
